/**
 * 产品介绍文档管理模块
 * 自动扫描 articles 目录下的 markdown 文件
 * 支持无限扩展产品介绍内容
 */

export interface ProductArticle {
  id: string;
  title: string;
  description: string;
  order: number;
  icon?: string;
  file: string;
  content: string;
}

export interface ProductFrontmatter {
  title: string;
  description: string;
  order: number;
  icon?: string;
}

/**
 * 解析 frontmatter
 */
function parseFrontmatter(content: string): { frontmatter: ProductFrontmatter; body: string } {
  const frontmatterRegex = /^---\s*\n([\s\S]*?)\n---\s*\n([\s\S]*)$/;
  const match = content.match(frontmatterRegex);

  if (!match) {
    return {
      frontmatter: { 
        title: 'Untitled', 
        description: '', 
        order: 999 
      },
      body: content
    };
  }

  const frontmatterText = match[1];
  const body = match[2];

  // 简单解析 YAML
  const frontmatter: ProductFrontmatter = {
    title: 'Untitled',
    description: '',
    order: 999
  };

  frontmatterText.split('\n').forEach(line => {
    const colonIndex = line.indexOf(':');
    if (colonIndex > 0) {
      const key = line.slice(0, colonIndex).trim();
      let value = line.slice(colonIndex + 1).trim();

      if (key === 'order') {
        frontmatter.order = parseInt(value) || 999;
      } else if (key === 'icon') {
        frontmatter.icon = value.replace(/["']/g, '');
      } else {
        (frontmatter as any)[key] = value.replace(/["']/g, '');
      }
    }
  });

  return { frontmatter, body };
}

/**
 * 从文件路径生成 ID
 */
function generateId(filePath: string): string {
  const filename = filePath.split('/').pop() || filePath.split('\\').pop() || '';
  return filename.replace(/\.md$/, '');
}

/**
 * 加载所有产品介绍文章
 * 使用 Vite 的 import.meta.glob 自动扫描
 */
export async function loadAllProductArticles(): Promise<ProductArticle[]> {
  // Vite 自动扫描所有 markdown 文件
  const articleFiles = import.meta.glob('./articles/**/*.md', { as: 'raw' });

  const articles: ProductArticle[] = [];

  for (const [path, loader] of Object.entries(articleFiles)) {
    try {
      const content = await loader() as string;
      const { frontmatter, body } = parseFrontmatter(content);

      articles.push({
        id: generateId(path),
        title: frontmatter.title,
        description: frontmatter.description,
        order: frontmatter.order,
        icon: frontmatter.icon,
        file: path,
        content: body
      });
    } catch (error) {
      console.error(`Failed to load product article: ${path}`, error);
    }
  }

  // 按 order 排序
  articles.sort((a, b) => a.order - b.order);

  return articles;
}

/**
 * 根据 ID 加载单篇文章
 */
export async function loadProductArticleById(id: string): Promise<ProductArticle | null> {
  const articles = await loadAllProductArticles();
  return articles.find(article => article.id === id) || null;
}

/**
 * 获取所有文章标题（用于导航）
 */
export async function getProductArticleIndex(): Promise<Pick<ProductArticle, 'id' | 'title' | 'description' | 'order' | 'icon'>[]> {
  const articles = await loadAllProductArticles();
  return articles.map(({ id, title, description, order, icon }) => ({
    id, title, description, order, icon
  }));
}
